import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import ultrasoundMain from '../Images/USG.jpg';
import ultrasoundAlt from '../Images/Portable Ultrasound Machine.jpeg';
import heroImage from '../Images/ultrahero.jpg';
import Footer from '../Pages/Footer';

const imageDetails = {
  [ultrasoundAlt]: {
    category: 'Color Dopplers & Ultrasound',
    heading: 'Ultrasound Scanning Machine (Portable Series)',
    title: 'Model - MM CD 200',
    description:
      'The Portable Ultrasound system is a compact and high-performance ultrasound imaging system designed for accurate diagnostics, portability, and ease of use in hospitals, clinics, emergency care, and point-of-care applications.',
    sections: {
      'Advanced Imaging Technologies': {
        icon: '🔬',
        items: [
          'TAI - Tissue Adaptive Imaging',
          'eSRI - Adaptive Speckle Reduction Imaging',
          'Frequency Compounding Imaging',
          'Adaptive Spatial Compounding Imaging',
          'Harmonic Imaging',
          'B mode Auto Optimization',
          'Digital Multi-Beam forming',
          'Trapezoid Imaging',
          'Adaptive Doppler Imaging',
          'Spectrum Enhancement,',
          'Digital Zoom,Auto Doppler Trace',
        ],
      },

      'Imaging Modes': {
        icon: '📷',
        items: [
          'B-mode',
          'M-mode',
          'Color Doppler',
          'PDI / DPDI',
          'PW Doppler',
          'CW Doppler',
        ],
      },

      'High Resolution Display': {
        icon: '📺',
        items: [
          '15.6" high resolution LCD monitor',
          'Full HD Resolution: 1920 x 1080',
          'Image Size: 1040 x 780',
          'Open angle: 0° - 180°',
          'Magnetic latch closure',
          'Built-in stereo speaker',
          'Brightness and Contrast adjustable',
          '10.1" Touch screen',
          'Gesture-control',
          'Virtual TGC sliders',
          'Support QWERTY keyboard for text input',
        ],
      },

      'Compact & Portable Design': {
        icon: '🎒',
        items: [
          'Lightweight design: 375 mm × 380 mm × 58 mm',
          'Net Weight (No Battery): 4.2 kg',
          'Net Weight (1 Battery): 4.65 kg',
          'Single Transducer Port Design',
        ],
      },
    },
  },

  [ultrasoundMain]: {
    category: 'Color Dopplers & Ultrasound',
    heading: 'Ultrasound Scanning Machine (Trolley Based)',
    title: 'Model - MM CD 100',
    description:
      'The MM CD 100 is an advanced trolley-based ultrasound system designed for high-quality imaging, smooth workflow, and reliability in hospitals, clinics, and diagnostic centers.',
    sections: {
      'User Interface & Display': {
        icon: '🖥️',
        items: [
          '14" touch screen (1920 × 1080 resolution)',
          '20° tiltable touch screen and display',
          'Gesture-control support',
          '5 user-defined touch screen keys',
          'Adjustable brightness',
          'Touch screen configurable user-configurable UI',
          'Supports Chinese & English QWERTY and French AZERTY keyboards',
          'Retractable physical keyboard with targeted down-lighting',
        ],
      },

      'Mobility & Ergonomic Design': {
        icon: '🔧',
        items: [
          'Front and rear handles for easy mobility',
          'Articulating arm for flexible positioning',
          'Rear storage tray',
          'Control panel with electronic lift (20 cm)',
          '±90° left/right swivel control panel',
          'Display monitor with ±180° swivel',
          'Tiltable display monitor',
          'Interactive back-lighting',
        ],
      },

      'Workflow & User Assistance': {
        icon: '⚡',
        items: [
          'eLearn instruction tool for basic scanning and nerve blocks',
          'High-performance integrated audio speaker',
          '6 transducer holders integrated with thecontrol panel',
          '5 active transducer ports',
          'Programmable store keys',
          'Probe temperature monitoring (PAT & TIP)',
          'Plastic and rubber hard keys provide tactile feedback',
          'Physical trackball for precise control',
        ],
      },

      'Control Panel & Operation': {
        icon: '🎮',
        items: [
          '8-segment physical TGC sliders',
          'Electronic lift up/down: 20 cm',
          'Left/right swivel: ±90°',
          'Interactive back-lighting',
          'Programmable store keys',
          'Physical hard keys with tactile feedback',
          'Retractable keyboard with targeted down-lighting',
          'Resettable over-current breaker for voltage protection',
        ],
      },

      'Imaging Modes': {
        icon: '📷',
        items: [
          'B-mode',
          'M-mode',
          'Anatomic M-mode (1/2/3-line AMM and Curved AMM)',
          'Color M mode',
          'Color Doppler',
          'Velocity-based Color Doppler',
          'PDI (Power Doppler Imaging)',
          'DPDI (Directional Power Doppler Imaging)',
          // 'PW Doppler',
          // 'CW Doppler (Continuous Wave Doppler)',
          // 'B + CW',
          // 'B + TVI | Dual Live',
          // 'B + TVD | Duplex',
          // 'B + TVI + TVD (Triplex)',
          // 'B + TVM',
          // 'B + E',
        ],
      },

      'System Performance & Hardware': {
        icon: '💻',
        items: [
          'Physical Channels: 128',
          'Digital Channels: 5529600',
          'Gray Scale: 256',
          'Beam Forming Processor: 8 beams',
          'Hard Drive: i5 with 6 cores 32GB',
          'Operating System: 1TB SSD 64 bit Linux',
          'System Boot-up: About 50s',
          'Boot-up from Sleep: About 3s',
          'Shutdown: 18s',
        ],
      },
    },
  },
};

const PortableUltrasoundMachineDetail = () => {
  const allImages = [ultrasoundMain, ultrasoundAlt];

  const [mainImage, setMainImage] = useState(ultrasoundMain);
  const [relatedImages, setRelatedImages] = useState(
    allImages.filter((img) => img !== ultrasoundMain)
  );
  const [isMobile, setIsMobile] = useState(false);

  const savedScrollY = useRef(null);
  const introRef = useRef(null);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // FIX 1: Save and restore scroll position on image click
  const handleImageClick = (clickedImage) => {
    setMainImage(clickedImage);
    setRelatedImages(allImages.filter((img) => img !== clickedImage));

    setTimeout(() => {
      introRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  const detail = imageDetails[mainImage];
  const sections = detail?.sections || {};

  return (
    <>
      <Helmet>
        <title>Portable Ultrasound Machine | Mindron Meditech</title>
        <meta
          name="description"
          content="Explore portable and trolley-based ultrasound machines by Mindron Meditech including MM CD 100 and MM CD 200 with color Doppler, advanced imaging modes, high-resolution display, and diagnostic workflow support."
        />
        <link
          rel="canonical"
          href="https://www.mindronmeditech.com/products/portableultrasoundmachine"
        />
      </Helmet>

      {/* FIX 2: overflowX hidden on outermost wrapper */}
      <div
        style={{
          fontFamily: 'sans-serif',
          margin: 0,
          padding: 0,
          overflowX: 'hidden',
        }}
      >
        {/* HERO */}
        <div
          style={{
            height: isMobile ? '30vh' : '80vh',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <img
            src={heroImage}
            alt="Hero"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              display: 'block',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
            }}
          />
        </div>

        {/* PRODUCT INTRO */}
        <div
          ref={introRef}
          style={{
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '40px',
            padding: isMobile ? '32px 6%' : '48px 6%',
            alignItems: 'flex-start',
          }}
        >
          {/* Left: text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div
              style={{
                display: 'inline-block',
                backgroundColor: '#e8f0fe',
                color: '#1a56db',
                fontSize: '13px',
                fontWeight: '600',
                padding: '4px 14px',
                borderRadius: '20px',
                marginBottom: '14px',
              }}
            >
              {detail?.category}
            </div>

            <h1
              style={{
                fontSize: isMobile ? '26px' : '38px',
                fontWeight: '800',
                color: '#0a1628',
                margin: '0 0 8px',
                lineHeight: 1.2,
              }}
            >
              {detail?.title}
            </h1>

            <h2
              style={{
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '500',
                color: '#444',
                margin: '0 0 20px',
              }}
            >
              {detail?.heading}
            </h2>

            <p
              style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.8',
                maxWidth: '520px',
              }}
            >
              {detail?.description}
            </p>
          </div>

          {/* Right: product image */}
          <div
            style={{
              flex: '0 0 auto',
              width: isMobile ? '100%' : '340px',
              background: '#f4f6fb',
              borderRadius: '14px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={mainImage}
              alt="Ultrasound"
              style={{
                width: '100%',
                maxHeight: '260px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </div>
        </div>

        {/* KEY FEATURES GRID */}
        <div
          style={{
            padding: isMobile ? '16px 6% 40px' : '16px 6% 56px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '28px',
            }}
          >
            Key Features
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px',
            }}
          >
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div
                key={idx}
                style={{
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  padding: '22px 20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '14px',
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>

                  <h3
                    style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#0a1628',
                      margin: 0,
                    }}
                  >
                    {sectionName}
                  </h3>
                </div>

                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '18px',
                    listStyle: 'disc',
                  }}
                >
                  {sectionData.items.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: '13px',
                        color: '#555',
                        lineHeight: '1.8',
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* RELATED PRODUCTS */}
        <div
          style={{
            padding: isMobile ? '0 6% 48px' : '0 6% 60px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '24px',
            }}
          >
            Related Products
          </h2>

          {/* FIX 3: hide scrollbar completely */}
          <div
            style={{
              display: 'flex',
              gap: '20px',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingBottom: '4px',
            }}
          >
            {relatedImages.map((img, index) => (
              <div
                key={index}
                onClick={() => handleImageClick(img)}
                style={{
                  flex: '0 0 auto',
                  width: '220px',
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow =
                    '0 8px 20px rgba(0,0,0,0.13)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 8px rgba(0,0,0,0.07)';
                }}
              >
                <div
                  style={{
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '150px',
                  }}
                >
                  <img
                    src={img}
                    alt={`Related ${index}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '120px',
                      objectFit: 'contain',
                    }}
                  />
                </div>

                <div
                  style={{
                    background: '#001f4d',
                    padding: '10px 14px',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#fff',
                      fontWeight: '600',
                    }}
                  >
                    {imageDetails[img]?.title || `Product ${index + 1}`}
                  </p>

                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: '11px',
                      color: '#aac4ff',
                    }}
                  >
                    {imageDetails[img]?.category}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Footer />

        <style>{`
          @media (max-width: 768px) {
            .header-container {
              height: 30vh !important;
            }
          }

          *::-webkit-scrollbar {
            display: none;
          }

          * {
            scrollbar-width: none;
          }

          body {
            overflow-x: hidden;
          }
        `}</style>
      </div>
    </>
  );
};

export default PortableUltrasoundMachineDetail;